import React, { MouseEventHandler, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import urlParser from 'bloko/common/urlParser';
import { isAbsolute, isInternal } from './url';

const isModifiedEvent = (event: React.MouseEvent<HTMLAnchorElement>) =>
    !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
const MAIN_MOUSE_BUTTON = 0;

declare global {
    interface LuxRequest {
        origin: string;
    }
}

declare module 'react-redux' {
    interface DefaultRootState {
        analyticsParams: { hhtmSource: string };
        request: LuxRequest;
    }
}

interface LinkProps {
    /** URL */
    to: string;
    /** Контент */
    children?: React.ReactNode;
    /** Отключает обработку */
    disabled?: boolean;
    /** Отменяет корректирование позиции скролла при SPA-навигации */
    cancelScrollCorrection?: boolean;
    /** Признак ссылки, доступной роботам.
     * Таким ссылкам не добавляем hhtm-параметры, чтобы не делать дубли страниц.
     * Подробнее: https://webmaster.yandex.ru/blog/kak-nayti-dubli-stranits-s-neznachaschimi-get-parametrami
     */
    isSeoLink?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const defaultOnClick = () => {};

const Link = React.forwardRef<HTMLAnchorElement, LinkProps & Omit<React.ComponentPropsWithoutRef<'a'>, 'href'>>(
    ({ to, children, onClick = defaultOnClick, cancelScrollCorrection, isSeoLink = false, ...anchorProps }, ref) => {
        const dispatch = useDispatch();
        const hhtmSource = useSelector(({ analyticsParams }) => analyticsParams?.hhtmSource || null);
        const hasHhtmFrom = to.indexOf('hhtmFrom=') !== -1;

        // Нужно смотреть именно на origin, потому что request.host
        // неправильно работает на hh.ru.STAND.pyn.ru (значение остаётся hh.ru).
        // А в request.origin приходит https://hh.ru.STAND.pyn.ru
        const origin = useSelector(({ request }) => request?.origin || null);

        const internalOnClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
            (event) => {
                onClick && onClick(event);

                if (!event.defaultPrevented && event.button === MAIN_MOUSE_BUTTON && !isModifiedEvent(event)) {
                    event.preventDefault();
                    const state = cancelScrollCorrection ? { cancelScrollCorrection } : undefined;
                    dispatch(push(to, state));
                }
            },
            [cancelScrollCorrection, dispatch, onClick, to]
        );

        const isAbsoluteLink = isAbsolute(to);
        const isInternalLink = isInternal(to, origin);
        const shouldProcessClick = isInternalLink || (isAbsoluteLink && !origin);
        const shouldAddHhtm = isInternalLink && !isSeoLink;

        const { target, disabled } = anchorProps;
        const clickHandler = shouldProcessClick && !target && !disabled ? internalOnClick : onClick;

        let href = to;
        if (shouldAddHhtm && hhtmSource && !hasHhtmFrom) {
            try {
                const parsedUrl = urlParser(to);
                parsedUrl.params.hhtmFrom = hhtmSource;
                href = parsedUrl.href;
            } catch (e) {} // eslint-disable-line no-empty
        }

        return (
            <a {...anchorProps} href={href} onClick={clickHandler} ref={ref}>
                {children}
            </a>
        );
    }
);

export default Link;
